// src/lib/pageWindow.js — which page numbers to draw.
//
// Owner, 2026-09-02: "show all page numbes how many pages are there now we only see which page
// we are on". The pager printed „გვერდი 3 / 12" — you knew where you were and had to type a
// number to go anywhere. Now the numbers themselves are the control.
//
// ⚠️ "ALL the page numbers" is the ask, and it is right up to a point: 12 pages should all be
// visible. 200 should not — a strip that wraps over three lines is not a control, and on a
// phone it pushes the feed off the screen. So every page is shown while they fit, and past
// that the strip keeps the FIRST, the LAST, and a window around where you are, with gaps.
// The first and last are the two that matter most: "how many are there" is answered by the
// last number being visible, which is the other half of what was asked for.

/** A gap in the strip. Rendered as an ellipsis, never clickable. */
export const GAP = '…'

/**
 * @param {number} current  zero-based page the agent is on
 * @param {number} total    how many pages exist
 * @param {number} maxSlots how many entries the strip may hold, gaps included
 * @returns {Array<number|'…'>} zero-based page numbers, with GAP where pages are skipped
 */
export function pageWindow(current, total, maxSlots = 9) {
  const t = Math.max(0, Math.floor(total) || 0)
  if (t <= 0) return []
  const cur = Math.min(Math.max(0, Math.floor(current) || 0), t - 1)
  // Everything fits: show everything. This is the common case — most searches are a handful of
  // pages — and it is exactly what the owner asked for.
  if (t <= maxSlots) return Array.from({ length: t }, (_, i) => i)

  // ⚠️ The strip must be a CONSTANT width, or the numbers shift under the cursor as you page
  // and the next click lands on something you did not aim at. Two slots go to first and last,
  // two to the gaps; the rest is the window around the current page.
  const windowSize = Math.max(1, maxSlots - 4)
  let start = cur - Math.floor((windowSize - 1) / 2)
  let end = start + windowSize - 1
  // Near the ends there is no gap to draw on that side, so the window borrows the freed slots
  // rather than leaving a short strip.
  if (start <= 1) { start = 1; end = start + windowSize; }
  if (end >= t - 2) { end = t - 2; start = end - windowSize; }
  start = Math.max(1, start)
  end = Math.min(t - 2, end)

  const out = [0]
  if (start > 1) out.push(GAP)
  for (let i = start; i <= end; i++) out.push(i)
  if (end < t - 2) out.push(GAP)
  out.push(t - 1)
  return out
}
